import { Injectable } from '@angular/core';
import { CookieBarService } from 'ras-cookie-bar';
import { RasGtmService } from 'ras-gtm';
import { forkJoin, Observable, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { SessionInfoModel } from '../../models/start-up-config/session-info-model';
import { StartUpConfigUiService } from './start-up-config-ui.service';
import { DatePipe } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class GtmDatalayerUiService {
  private validationErrorSubject = new Subject<string>();
  public readonly validationError$ = this.validationErrorSubject.asObservable();

  constructor(
    private rasGtmService: RasGtmService,
    private cookieBarService: CookieBarService,
    private startUpConfigUiService: StartUpConfigUiService,
    private datePipe: DatePipe) { }

  public initGtmDatalayer(sessionInfo$: Observable<SessionInfoModel>, isWs: boolean): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      forkJoin(this.cookieBarService.getCookieConsent().pipe(take(1)), sessionInfo$.pipe(take(1))).subscribe(([, sessionInfo]) => {
        this.startUpConfigUiService.loadConsentScript$.subscribe((isLoaded: any) => {
          if (isLoaded) {
            this.addSessionTrackingId(sessionInfo.sessionTrackingId)
              .pipe(take(1))
              .subscribe({
                next: () => {
                  this.addPageType(isWs)
                    .pipe(take(1))
                    .subscribe({
                      next: () => {
                        this.rasGtmService
                          .addGtmToDom()
                          .pipe(take(1))
                          .subscribe({
                            next: () => {
                              subscriber.next(true);
                              subscriber.complete();
                            }
                          });
                      }
                    });
                }
              });
          }
        });
      });
    });
  }

  private addSessionTrackingId(sessionTrackingId: any): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({ sessionid: sessionTrackingId });
  }

  private addPageType(isWs: boolean): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      productCategory: 'insurance',
      productcg: 'health',
      testversion: '',
      typepage: isWs ? 'wise health' : 'cj health'
    });
  }

  public addVirtualPage(pageName: string, qKey: string, sectionIndex: number, questionText: string, abTestName: string, abTestValue: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: 'virtualpage',
      category: 'insurance',
      product: 'health',
      virtualpage: pageName,
      question: qKey ? qKey.toLowerCase() : null,
      questiontext: questionText,
      section: 'section' + sectionIndex,
      testversion: abTestName === undefined ? '' : abTestName + '_' + abTestValue === undefined ? '' : abTestValue
    });
  }

  public addVirtualPageValidationError(qKey: string, sectionIndex: number, errorSummary: string): Observable<boolean> {
    this.validationErrorSubject.next(qKey);
    return this.rasGtmService.pushOnDataLayer({
      event: 'virtualpage',
      category: 'insurance',
      product: 'health',
      virtualpage: 'customerjourneyerror',
      question: qKey ? qKey.toLowerCase() : null,
      cjerrormessage: errorSummary,
      section: 'section' + sectionIndex
    });
  }
  public addPreviousQuoteOnloadScreen(qKey: string, virtualpage: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: 'virtualpage',
      virtualpage: virtualpage,
      category: 'insurance',
      product: 'health',
      lastquotepopupscreen: qKey
    });
  }
  public addPreviousQuoteAnsware(qKey: string, answer: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: 'lastquotepopupanswer',
      lastquotepopupanswer: answer,
      lastquotepopupcategory: 'insurance',
      lastquotepopupproduct: 'health',
      lastquotepopupscreen: qKey
    });
  }

  public addAnswer(pageName: string, qKey: string, answerStr: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: pageName,
      category: 'insurance',
      product: 'health',
      questionanswer: qKey ? qKey.toLowerCase() : null,
      answer: answerStr
    });
  }

  public addAyudaFormEvent(channelValue: string, formData: any): Observable<boolean> {
    if (channelValue === 'tellamamos' && formData) {
      return this.rasGtmService.pushOnDataLayer({
        event: 'form',
        actionform: 'submit',
        form: 'ayuda',
        ayuda: channelValue,
        telephoneNumber: formData.callerTelephoneNumber,
        productform: 'health'
      });
    } else {
      return this.rasGtmService.pushOnDataLayer({
        event: 'form',
        actionform: 'submit',
        form: 'ayuda',
        ayuda: channelValue,
        productform: 'health'
      });
    }
  }

  public upaLoginEvent(loginId: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: 'login',
      loginID: loginId
    });
  }
  public addWiseSectionEvent(wiseSectionError: string, wiseSectionOrigin: string): Observable<boolean> {
    return this.rasGtmService.pushOnDataLayer({
      event: 'Wise Section',
      WiseSectionError: wiseSectionError,
      WiseSectionProduct: 'salud',
      WiseSectionType: 'Wise section',
      WiseSectionOrigin: wiseSectionOrigin
    });
  }

  public addEcomEvent(dataLayerGroup: string | undefined): Observable<boolean> {
    const data = {
      'firerule': 'EcomPayable',
      'event': 'Ecom',
      'pamount': '505',
      'payablectr': 'payablectr-Medico',
      'pname': 'Medico-TelephoneNumber-cj',
      'pnameType': 'web',
      'product': 'Medico',
      'section': 'SEGUROS',
      'type of user': 'Old',
      'unique transaction id': '' + this.datePipe.transform(new Date(), 'yyyyMMddhhmmssSSS'),
      'unitcode': '' + this.datePipe.transform(new Date(), 'yyyyMMddhhmmssSSS'),
      'webname': 'rastreator.com',
      'insurer': 'RASTREATOR'
    };
    if(dataLayerGroup) {
      return this.rasGtmService.pushOnDataLayer({...data, 'group': dataLayerGroup});
    }
    return this.rasGtmService.pushOnDataLayer(data);
  }
}
